import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { DEMO_DASHBOARD, DEMO_QUEUED_MESSAGES, DEMO_LINK_CLICKS, DEMO_SENT_MESSAGES } from '../demoData';
import { filterChannelAccess, isChannelVisible } from '../config/featureFlags';
import { formatDate } from '../utils/formatDate';
import { getMessageStatus, getMessageStatusLabel, getMessageStatusStyle } from '../utils/messageStatus';

const TIME_RANGES = [
  { value: '1w', label: '1 Week' },
  { value: '1m', label: '1 Month' },
  { value: '3m', label: '3 Months' },
  { value: '1y', label: '1 Year' },
  { value: 'total', label: 'All Time' },
];

function UsageBar({ label, used, limit, color, icon, available = true }) {
  if (!available) {
    return (
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 12, padding: '16px 20px', opacity: 0.6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 18 }}>{icon}</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
          <span style={{
            fontSize: 11, background: '#F3F4F6', color: '#6B7280',
            padding: '2px 8px', borderRadius: 12, fontWeight: 500, marginLeft: 'auto',
          }}>
            Not available
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          This channel is unavailable for your account or region
        </div>
      </div>
    );
  }

  const isUnlimited = limit === null || limit === Infinity || limit === 'Infinity';
  const percent = isUnlimited ? 0 : limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const isWarning = !isUnlimited && percent >= 80;
  const isLimit = !isUnlimited && percent >= 100;

  const barColor = isLimit ? '#EF4444' : isWarning ? '#F59E0B' : color;
  const displayLimit = isUnlimited ? 'Unlimited' : limit.toLocaleString();
  const remaining = isUnlimited ? 'Unlimited' : Math.max(0, limit - used).toLocaleString();

  return (
    <div style={{
      background: 'var(--surface)', border: `1px solid ${isLimit ? '#FECACA' : isWarning ? '#FDE68A' : 'var(--border)'}`,
      borderRadius: 12, padding: '16px 20px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>{icon}</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: barColor }}>
          {used.toLocaleString()} / {displayLimit}
        </span>
      </div>
      {!isUnlimited && (
        <div style={{ background: 'var(--bg)', borderRadius: 6, height: 6, overflow: 'hidden', marginBottom: 6 }}>
          <div style={{
            height: '100%', width: `${Math.min(percent, 100)}%`,
            background: barColor, borderRadius: 6, transition: 'width 0.6s ease',
          }} />
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)' }}>
        <span>{remaining} remaining</span>
        {isLimit && <span style={{ color: '#EF4444', fontWeight: 600 }}>Limit reached</span>}
        {isWarning && !isLimit && <span style={{ color: '#F59E0B', fontWeight: 600 }}>Nearing limit</span>}
      </div>
    </div>
  );
}

function ChannelCard({ icon, label, sent, clicked, color, bgColor }) {
  const ctr = sent > 0 ? Math.round((clicked / sent) * 100) : 0;

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 14,
      padding: 24,
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, background: bgColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
        }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{label}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Channel performance</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>{sent}</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>Sent</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>{clicked}</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>Clicked</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color }}>{ctr}%</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>Click-through Rate</div>
        </div>
      </div>

      <div style={{ background: 'var(--bg)', borderRadius: 8, height: 8, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${Math.min(ctr, 100)}%`,
          background: color,
          borderRadius: 8,
          transition: 'width 0.6s ease',
        }} />
      </div>
    </div>
  );
}

function CTRChart({ channels, channelAccess }) {
  const maxCTR = Math.max(
    channels.email.sent > 0 ? (channels.email.clicked / channels.email.sent) * 100 : 0,
    channels.sms.sent > 0 ? (channels.sms.clicked / channels.sms.sent) * 100 : 0,
    isChannelVisible('whatsapp') && channels.whatsapp.sent > 0 ? (channels.whatsapp.clicked / channels.whatsapp.sent) * 100 : 0,
    10
  );

  const barData = [
    { key: 'email', label: 'Email', color: '#3B82F6', ...channels.email, access: true },
    { key: 'sms', label: 'SMS', color: '#10B981', ...channels.sms, access: channelAccess.sms },
    ...(isChannelVisible('whatsapp') ? [{ key: 'whatsapp', label: 'WhatsApp', color: '#22C55E', ...channels.whatsapp, access: channelAccess.whatsapp }] : []),
  ].filter(c => c.access);

  if (barData.every(c => c.sent === 0)) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
        <div style={{ fontSize: 14 }}>No message data yet for CTR calculation</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {barData.map(ch => {
          const ctr = ch.sent > 0 ? (ch.clicked / ch.sent) * 100 : 0;
          const barWidth = maxCTR > 0 ? (ctr / maxCTR) * 100 : 0;
          return (
            <div key={ch.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{ch.label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: ch.color }}>{ctr.toFixed(1)}%</span>
              </div>
              <div style={{ background: 'var(--bg)', borderRadius: 6, height: 24, overflow: 'hidden', position: 'relative' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(barWidth, 100)}%`,
                  background: `linear-gradient(90deg, ${ch.color}CC, ${ch.color})`,
                  borderRadius: 6,
                  transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                  minWidth: ctr > 0 ? 24 : 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: 8,
                }}>
                  {ctr > 5 && (
                    <span style={{ fontSize: 11, color: 'white', fontWeight: 600 }}>
                      {ch.clicked}/{ch.sent}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActivityDetailModal({ activity, onClose, isDemo }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activity) return;
    if (isDemo) {
      setDetail({
        tracking: { totalClicks: activity.type === 'email' ? 2 : 0, lastClickedAt: activity.sentAt },
        template: {
          subject: activity.type === 'email' ? 'We would love to see you again!' : null,
          body: 'Thanks for visiting Glow Beauty Salon! We would love to welcome you back soon.',
        },
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    api.getActivityDetail(activity.id)
      .then(setDetail)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activity, isDemo]);

  if (!activity) return null;

  const channelIcon = activity.type === 'email' ? '📧' : activity.type === 'whatsapp' ? '📱' : '💬';
  const channelLabel = activity.type.charAt(0).toUpperCase() + activity.type.slice(1);
  const deliveryLabel = getDeliveryLabel(activity);
  const statusColor = getMessageStatusStyle(activity).color;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560, maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0 }}>Message Detail</h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <span className="spinner" />
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Channel</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{channelIcon} {channelLabel}</div>
              </div>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Status</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: statusColor }}>
                  {deliveryLabel}
                </div>
              </div>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Recipient</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{activity.customer?.name || 'Unknown'}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
                  {activity.customer?.email || activity.customer?.phone || ''}
                </div>
              </div>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Type</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {formatActivityCategory(activity.category, activity.messageType)}
                </div>
              </div>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Sent At</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{formatDate(activity.sentAt)}</div>
              </div>
            </div>

            {detail?.tracking && !['Failed', 'Bounced', 'Complained'].includes(deliveryLabel) && (
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#166534', marginBottom: 4 }}>Link Tracking</div>
                <div style={{ fontSize: 13, color: '#166534' }}>
                  {detail.tracking.totalClicks} click{detail.tracking.totalClicks !== 1 ? 's' : ''}
                  {detail.tracking.lastClickedAt && ` · Last clicked ${formatDate(detail.tracking.lastClickedAt)}`}
                </div>
              </div>
            )}

            {detail?.template && (
              <div style={{ marginTop: 16 }}>
                {detail.template.subject && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Subject</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{detail.template.subject}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Message Content</div>
                  <div style={{
                    background: 'var(--bg)', border: '1px solid var(--border)',
                    borderRadius: 8, padding: 16, fontSize: 13, lineHeight: 1.6,
                    color: 'var(--text)', whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto',
                  }}>
                    {detail.template.body}
                  </div>
                </div>
              </div>
            )}

            {!detail?.template && (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>
                Message template content not available
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function QueuedMessagesModal({ range, onClose, onCountUpdate, isDemo }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isDemo) {
      setItems(DEMO_QUEUED_MESSAGES.items);
      setTotal(DEMO_QUEUED_MESSAGES.total);
      setLoading(false);
      return;
    }

    api.getQueuedMessages(range)
      .then(res => {
        const count = res.total || 0;
        setItems(res.items || []);
        setTotal(count);
        if (onCountUpdate) onCountUpdate(count);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [range, onCountUpdate, isDemo]);

  const channelIcon = type => type === 'email' ? '📧' : type === 'whatsapp' ? '📱' : '💬';
  const categoryLabel = cat => {
    const labels = { review_request: 'Review Request', reminder: 'Reminder', thank_you: 'Thank You' };
    return labels[cat] || cat?.replaceAll('_', ' ') || 'Message';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620, maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0 }}>Queued Messages</h2>
            {!loading && <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{total} message{total !== 1 ? 's' : ''} scheduled</p>}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <span className="spinner" />
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
            <div style={{ fontSize: 14 }}>No queued messages</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map(item => (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px', borderRadius: 10,
                background: 'var(--bg)', border: '1px solid var(--border)',
              }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: item.type === 'email' ? '#DBEAFE' : item.type === 'sms' ? '#D1FAE5' : '#DCFCE7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
                }}>
                  {channelIcon(item.type)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                    {categoryLabel(item.category)}
                    <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> via {item.type}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                    To: {item.customer?.name || 'Unknown'}
                    {item.customer?.email ? ` (${item.customer.email})` : item.customer?.phone ? ` (${item.customer.phone})` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)' }}>
                    {formatDate(item.scheduledAt)}
                  </div>
                  <div style={{
                    fontSize: 10, marginTop: 3, padding: '2px 8px', borderRadius: 10,
                    background: '#FEF3C7', color: '#92400E', fontWeight: 600, display: 'inline-block',
                  }}>
                    Queued
                  </div>
                </div>
              </div>
            ))}
            {total > items.length && (
              <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>
                Showing {items.length} of {total} queued messages
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LinkClicksModal({ range, onClose, isDemo }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isDemo) {
      setItems(DEMO_LINK_CLICKS.items);
      setTotal(DEMO_LINK_CLICKS.total);
      setLoading(false);
      return;
    }

    api.getLinkClicks(range)
      .then(res => {
        setItems(res.items || []);
        setTotal(res.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [range, isDemo]);

  const channelIcon = ch => ch === 'email' ? '📧' : ch === 'whatsapp' ? '📱' : '💬';
  const channelColor = ch => ch === 'email' ? '#DBEAFE' : ch === 'sms' ? '#D1FAE5' : '#DCFCE7';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 650, maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0 }}>Link Clicks</h2>
            {!loading && <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{total} total click{total !== 1 ? 's' : ''}</p>}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <span className="spinner" />
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔗</div>
            <div style={{ fontSize: 14 }}>No link clicks yet</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map(item => (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px', borderRadius: 10,
                background: 'var(--bg)', border: '1px solid var(--border)',
              }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: channelColor(item.channel),
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
                }}>
                  {channelIcon(item.channel)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                    {item.customerName || 'Unknown'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.customerEmail || item.customerPhone || ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 10,
                    background: channelColor(item.channel), color: 'var(--text)', fontWeight: 600, display: 'inline-block', marginBottom: 4,
                  }}>
                    {item.channel}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {formatDate(item.clickedAt)}
                  </div>
                </div>
              </div>
            ))}
            {total > items.length && (
              <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>
                Showing {items.length} of {total} clicks
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SentMessagesModal({ range, onClose, isDemo }) {
  const PAGE_SIZE = 50;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPage(1);
  }, [range, isDemo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    if (isDemo) {
      const allItems = DEMO_SENT_MESSAGES.items;
      const start = (page - 1) * PAGE_SIZE;
      if (!cancelled) {
        setItems(allItems.slice(start, start + PAGE_SIZE));
        setTotal(allItems.length);
        setTotalPages(Math.ceil(allItems.length / PAGE_SIZE));
        setLoading(false);
      }
      return () => { cancelled = true; };
    }

    api.getSentMessages(range, page, PAGE_SIZE)
      .then(res => {
        if (cancelled) return;
        setItems(res.items || []);
        setTotal(res.total || 0);
        setTotalPages(res.totalPages ?? Math.ceil((res.total || 0) / PAGE_SIZE));
      })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, isDemo, page]);

  const channelIcon = type => type === 'email' ? '📧' : type === 'whatsapp' ? '📱' : '💬';
  const channelColor = type => type === 'email' ? '#DBEAFE' : type === 'sms' ? '#D1FAE5' : '#DCFCE7';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 650, maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0 }}>Messages Sent</h2>
            {!loading && <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{total} message{total !== 1 ? 's' : ''} sent</p>}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <span className="spinner" />
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📨</div>
            <div style={{ fontSize: 14 }}>No messages sent yet</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map(item => (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px', borderRadius: 10,
                background: 'var(--bg)', border: '1px solid var(--border)',
              }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: channelColor(item.type),
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
                }}>
                  {channelIcon(item.type)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                    {formatActivityCategory(item.category, item.messageType)}
                    <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> via {item.type}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                    To: {item.customer?.name || 'Unknown'}
                    {item.customer?.email ? ` (${item.customer.email})` : item.customer?.phone ? ` (${item.customer.phone})` : ''}
                  </div>
                  {item.subject && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340 }}>
                      {item.subject}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {formatDate(item.sentAt)}
                  </div>
                  <div style={{
                    fontSize: 10, marginTop: 3, padding: '2px 8px', borderRadius: 10,
                    fontWeight: 600, display: 'inline-block',
                    ...getMessageStatusStyle(item),
                  }}>
                    {getDeliveryLabel(item)}
                  </div>
                </div>
              </div>
            ))}
            {total > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 0 4px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Showing {(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + items.length} of {total} messages
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="btn btn-secondary btn-sm" type="button" disabled={loading || page <= 1} onClick={() => setPage(current => Math.max(1, current - 1))}>
                    Previous
                  </button>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 78, textAlign: 'center' }}>
                    Page {page} of {Math.max(totalPages, 1)}
                  </span>
                  <button className="btn btn-secondary btn-sm" type="button" disabled={loading || page >= totalPages} onClick={() => setPage(current => current + 1)}>
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SkeletonPulse({ width, height, borderRadius = 4, style = {} }) {
  return (
    <div style={{
      width, height, borderRadius, background: 'var(--bg)',
      animation: 'pulse 1.5s ease-in-out infinite',
      ...style,
    }} />
  );
}

function SkeletonStatCard() {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '20px 16px', textAlign: 'center',
      boxShadow: 'var(--shadow-sm)',
    }}>
      <SkeletonPulse width={24} height={24} borderRadius={4} style={{ margin: '0 auto 8px' }} />
      <SkeletonPulse width={40} height={28} borderRadius={4} style={{ margin: '0 auto' }} />
      <SkeletonPulse width={80} height={12} borderRadius={4} style={{ margin: '4px auto 0' }} />
      <SkeletonPulse width={72} height={11} borderRadius={4} style={{ margin: '6px auto 0' }} />
    </div>
  );
}

function SkeletonUsageBar() {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '16px 20px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SkeletonPulse width={18} height={18} borderRadius={4} />
          <SkeletonPulse width={60} height={14} borderRadius={4} />
        </div>
        <SkeletonPulse width={72} height={13} borderRadius={4} />
      </div>
      <SkeletonPulse width="100%" height={6} borderRadius={6} style={{ marginBottom: 6 }} />
      <SkeletonPulse width={90} height={12} borderRadius={4} />
    </div>
  );
}

function SkeletonChannelCard() {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 14, padding: 24, boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <SkeletonPulse width={44} height={44} borderRadius={12} />
        <div>
          <SkeletonPulse width={64} height={16} borderRadius={4} style={{ marginBottom: 6 }} />
          <SkeletonPulse width={100} height={12} borderRadius={4} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{ textAlign: 'center' }}>
            <SkeletonPulse width={36} height={24} borderRadius={4} style={{ margin: '0 auto 4px' }} />
            <SkeletonPulse width={28} height={11} borderRadius={4} style={{ margin: '0 auto' }} />
          </div>
        ))}
      </div>
      <SkeletonPulse width="100%" height={8} borderRadius={8} />
    </div>
  );
}

function SkeletonActivityItem({ showBorder }) {
  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'center', padding: '14px 8px',
      borderBottom: showBorder ? '1px solid var(--border)' : 'none',
    }}>
      <SkeletonPulse width={36} height={36} borderRadius={8} />
      <div style={{ flex: 1 }}>
        <SkeletonPulse width="60%" height={12} borderRadius={4} style={{ marginBottom: 8 }} />
        <SkeletonPulse width="40%" height={10} borderRadius={4} />
      </div>
      <SkeletonPulse width={56} height={20} borderRadius={10} />
    </div>
  );
}

function formatActivityCategory(category, messageType) {
  if (messageType === 'custom' || category?.startsWith('custom_')) {
    return 'Custom';
  }
  const labels = {
    review_request: 'Review Request',
    reminder: 'Reminder',
    thank_you: 'Thank You',
  };
  return labels[category] || category?.replaceAll('_', ' ') || 'Message';
}

function getDeliveryLabel(item) {
  return getMessageStatusLabel(item);
}

function getDeliveryBadgeClass(item) {
  const status = getMessageStatus(item);
  if (status === 'delivered') return 'badge-success';
  return 'badge-warning';
}

function getDeliveryBadgeStyle(item) {
  return getMessageStatusStyle(item);
}

export default function Dashboard() {
  const { user, isDemo } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [range, setRange] = useState('total');
  const [activityItems, setActivityItems] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityPage, setActivityPage] = useState(1);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showQueued, setShowQueued] = useState(false);
  const [showLinkClicks, setShowLinkClicks] = useState(false);
  const [showSentMessages, setShowSentMessages] = useState(false);
  const [queuedCountOverride, setQueuedCountOverride] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const ACTIVITY_PAGE_SIZE = 10;
  const hasLoadedStats = useRef(false);
  const hasLoadedActivity = useRef(false);

  const fetchActivity = useCallback((page = 1) => {
    if (isDemo) {
      setActivityItems(DEMO_DASHBOARD.recentActivity);
      setActivityTotal(DEMO_DASHBOARD.recentActivity.length);
      setActivityLoading(false);
      return;
    }

    setActivityLoading(true);
    const offset = (page - 1) * ACTIVITY_PAGE_SIZE;
    api.getDashboardActivity(offset, ACTIVITY_PAGE_SIZE)
      .then((res) => {
        setActivityItems(res.items || []);
        setActivityTotal(res.total || 0);
      })
      .catch(console.error)
      .finally(() => setActivityLoading(false));
  }, [isDemo]);

  const fetchStats = useCallback((timeRange, isInitial = false) => {
    if (isDemo) {
      setData(DEMO_DASHBOARD);
      setActivityItems(DEMO_DASHBOARD.recentActivity);
      setActivityTotal(DEMO_DASHBOARD.recentActivity.length);
      setStatsLoading(false);
      setActivityLoading(false);
      return;
    }

    if (isInitial) {
      setStatsLoading(true);
    } else {
      setRangeLoading(true);
    }

    api.getDashboard(timeRange, false)
      .then(d => { setData(d); setQueuedCountOverride(null); })
      .catch(console.error)
      .finally(() => {
        setStatsLoading(false);
        setRangeLoading(false);
      });
  }, [isDemo]);

  useEffect(() => {
    const isInitial = !hasLoadedStats.current;
    hasLoadedStats.current = true;
    fetchStats(range, isInitial);
  }, [range, fetchStats]);

  useEffect(() => {
    if (hasLoadedActivity.current) return;
    hasLoadedActivity.current = true;
    fetchActivity(1);
  }, [fetchActivity]);

  const goToPage = useCallback((page) => {
    if (activityLoading || isDemo) return;
    setActivityPage(page);
    fetchActivity(page);
  }, [activityLoading, isDemo, fetchActivity]);

  const handleRefresh = useCallback(() => {
    if (refreshing) return;
    setRefreshing(true);
    const statsPromise = isDemo
      ? Promise.resolve()
      : api.getDashboard(range, false).then(d => { setData(d); setQueuedCountOverride(null); });
    const activityPromise = isDemo
      ? Promise.resolve()
      : api.getDashboardActivity(0, ACTIVITY_PAGE_SIZE).then(res => {
          setActivityItems(res.items || []);
          setActivityTotal(res.total || 0);
          setActivityPage(1);
        });
    Promise.all([statsPromise, activityPromise])
      .catch(console.error)
      .finally(() => setRefreshing(false));
  }, [refreshing, isDemo, range]);

  if (statsLoading && !data) {
    return (
      <div>
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>Dashboard</h1>
          <SkeletonPulse width={100} height={36} borderRadius={8} />
        </div>

        <div className="section">
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2>Usage & Limits</h2>
              <SkeletonPulse width={200} height={13} borderRadius={4} style={{ marginTop: 4 }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            <SkeletonUsageBar /><SkeletonUsageBar /><SkeletonUsageBar />
          </div>
        </div>

        <div className="section">
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h2>Overview</h2>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Overview of your customer engagement</p>
            </div>
            <div style={{ display: 'flex', gap: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 4 }}>
              {TIME_RANGES.map(t => (
                <div
                  key={t.value}
                  style={{
                    padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                    background: t.value === 'total' ? 'var(--primary)' : 'transparent',
                    color: t.value === 'total' ? 'white' : 'var(--text-secondary)',
                  }}
                >
                  {t.label}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 32 }}>
            <SkeletonStatCard /><SkeletonStatCard /><SkeletonStatCard />
          </div>
        </div>

        <div className="section">
          <div className="section-header">
            <h2>Channel Performance</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
            <SkeletonChannelCard /><SkeletonChannelCard />
          </div>
        </div>

        <div className="section">
          <div className="section-header">
            <h2>Recent Activity</h2>
          </div>
          <div className="card">
            <div style={{ padding: '24px 8px' }}>
              <SkeletonActivityItem showBorder />
              <SkeletonActivityItem showBorder />
              <SkeletonActivityItem showBorder={false} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const usage = data?.usage;
  const subscription = data?.subscription;
  const accountPaused = !isDemo && subscription?.isPaymentFailed;
  const channelAccess = filterChannelAccess(data?.channelAccess || user?.channelAccess || { email: true, sms: false, whatsapp: false });
  const plan = data?.plan || user?.plan || 'free';
  const totalSent = (data?.emailsSent ?? 0)
    + (channelAccess.sms ? (data?.smsSent ?? 0) : 0)
    + (isChannelVisible('whatsapp') ? (data?.whatsappSent ?? 0) : 0);
  const channels = data?.channels ?? { email: { sent: 0, clicked: 0 }, sms: { sent: 0, clicked: 0 }, whatsapp: { sent: 0, clicked: 0 } };
  const totalClicked = channels.email.clicked
    + (channelAccess.sms ? channels.sms.clicked : 0)
    + (isChannelVisible('whatsapp') ? channels.whatsapp.clicked : 0);

  return (
    <div>
      {accountPaused && (
        <div style={{
          background: '#FFF1F2', border: '1px solid #FDA4AF', borderLeft: '5px solid #E11D48',
          borderRadius: 12, padding: '18px 22px', marginBottom: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
        }}>
          <div>
            <div style={{ color: '#9F1239', fontSize: 16, fontWeight: 750, marginBottom: 5 }}>
              Your renewal payment has not been confirmed.
            </div>
            <div style={{ color: '#881337', fontSize: 13, lineHeight: 1.5 }}>
              The Free plan and its message quotas apply until Razorpay confirms a successful payment. Your data and automation settings are retained.
            </div>
          </div>
          <button
            onClick={() => navigate('/subscription')}
            className="btn"
            style={{ background: '#E11D48', color: 'white', border: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            Resume Subscription
          </button>
        </div>
      )}
      {plan === 'free' && !isDemo && (
        <div style={{
          background: 'linear-gradient(135deg, #6C5CE7 0%, #a855f7 100%)',
          borderRadius: 14,
          padding: '20px 28px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          color: 'white',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
              Unlock more with a paid plan
            </div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              You're on the Free plan with 5 emails and up to 2 customer records per month. Upgrade for higher limits.
            </div>
          </div>
          <button
            onClick={() => navigate('/subscription')}
            style={{
              background: 'white',
              color: '#6C5CE7',
              border: 'none',
              borderRadius: 8,
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'transform 0.15s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            View Plans
          </button>
        </div>
      )}

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Dashboard</h1>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh dashboard"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600,
            color: 'var(--text-secondary)', cursor: refreshing ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s ease', opacity: refreshing ? 0.7 : 1,
          }}
          onMouseEnter={e => { if (!refreshing) e.currentTarget.style.borderColor = 'var(--primary)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
        >
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {usage && (
        <div className="section">
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2>Usage & Limits</h2>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                {plan.charAt(0).toUpperCase() + plan.slice(1)} plan
                {usage.periodStart && ` · Billing cycle started ${formatDate(usage.periodStart)}`}
              </p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            <UsageBar
              label="Customers" icon="👥"
              used={usage.customers?.used ?? 0}
              limit={usage.customers?.limit ?? 0}
              color="#6C5CE7"
            />
            <UsageBar
              label="Emails" icon="📧"
              used={usage.email?.used ?? 0}
              limit={usage.email?.limit ?? 0}
              color="#3B82F6"
            />
            {channelAccess.sms && (
            <UsageBar
              label="SMS" icon="💬"
              used={usage.sms?.used ?? 0}
              limit={usage.sms?.limit ?? 0}
              color="#10B981"
            />
            )}
            {isChannelVisible('whatsapp') && (
            <UsageBar
              label="WhatsApp" icon="📱"
              used={usage.whatsapp?.used ?? 0}
              limit={usage.whatsapp?.limit ?? 0}
              color="#22C55E"
              available={channelAccess.whatsapp}
            />
            )}
          </div>

          {isChannelVisible('whatsapp') && channelAccess.whatsapp && !data?.whatsappConnected && (
            <div style={{
              marginTop: 12, padding: '10px 16px', background: '#DCFCE7',
              border: '1px solid #86EFAC', borderRadius: 8, fontSize: 13, color: '#166534',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span>Connect your WhatsApp Business account to start sending WhatsApp messages.</span>
              <button
                className="btn btn-sm"
                style={{ background: '#25D366', color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}
                onClick={() => navigate('/settings')}
              >
                Connect
              </button>
            </div>
          )}
        </div>
      )}

      <div className="section">
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2>Overview</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Overview of your customer engagement</p>
          </div>
          <div style={{ display: 'flex', gap: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 4, opacity: rangeLoading ? 0.7 : 1 }}>
            {TIME_RANGES.map(t => (
              <button
                key={t.value}
                onClick={() => setRange(t.value)}
                disabled={rangeLoading}
                style={{
                  padding: '6px 12px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 500,
                  cursor: 'pointer', transition: 'all 0.15s ease',
                  background: range === t.value ? 'var(--primary)' : 'transparent',
                  color: range === t.value ? 'white' : 'var(--text-secondary)',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 16,
          marginBottom: 32,
          opacity: rangeLoading ? 0.65 : 1,
          transition: 'opacity 0.15s ease',
        }}>
          {[
            { icon: '📨', value: totalSent, label: 'Messages Sent', onClick: () => setShowSentMessages(true) },
            { icon: '🔗', value: totalClicked, label: 'Link Clicks', onClick: () => setShowLinkClicks(true) },
            { icon: '⏰', value: queuedCountOverride ?? data?.scheduledReminders ?? 0, label: 'Queued Messages', onClick: () => setShowQueued(true) },
          ].map((s, i) => (
            <div
              key={i}
              onClick={s.onClick}
              style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 12, padding: '20px 16px', textAlign: 'center',
                boxShadow: 'var(--shadow-sm)', cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(99,102,241,0.15), var(--shadow-sm)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
              }}
            >
              <div style={{ fontSize: 24, marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>
                {s.value}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, fontWeight: 500 }}>{s.label}</div>
              <div style={{
                fontSize: 11, color: 'var(--primary)', marginTop: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                fontWeight: 500, opacity: 0.85,
              }}>
                Click to view <span style={{ fontSize: 13 }}>›</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="section" style={{ opacity: rangeLoading ? 0.65 : 1, transition: 'opacity 0.15s ease' }}>
        <div className="section-header">
          <h2>Channel Performance</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
          <ChannelCard
            icon="📧" label="Email"
            sent={channels.email.sent} clicked={channels.email.clicked}
            color="#3B82F6" bgColor="#DBEAFE"
          />
          {channelAccess.sms && (
            <ChannelCard
              icon="💬" label="SMS"
              sent={channels.sms.sent} clicked={channels.sms.clicked}
              color="#10B981" bgColor="#D1FAE5"
            />
          )}
          {isChannelVisible('whatsapp') && channelAccess.whatsapp && (
            <ChannelCard
              icon="📱" label="WhatsApp"
              sent={channels.whatsapp.sent} clicked={channels.whatsapp.clicked}
              color="#22C55E" bgColor="#DCFCE7"
            />
          )}
        </div>
      </div>

      <div className="section">
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>Recent Activity</h2>
            {activityTotal > 0 && (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                {activityTotal} activit{activityTotal !== 1 ? 'ies' : 'y'} in the last 30 days
              </p>
            )}
          </div>
        </div>
        <div className="card">
          {activityLoading && activityItems.length === 0 ? (
            <div style={{ padding: '24px 8px' }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 0', borderBottom: i < 3 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--bg)' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 12, width: '60%', background: 'var(--bg)', borderRadius: 4, marginBottom: 8 }} />
                    <div style={{ height: 10, width: '40%', background: 'var(--bg)', borderRadius: 4 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : activityItems.length > 0 ? (
            <>
              <div style={{
                opacity: activityLoading ? 0.45 : 1,
                transition: 'opacity 0.2s ease',
                pointerEvents: activityLoading ? 'none' : 'auto',
              }}>
              <ul className="activity-list">
                {activityItems.filter(item => {
                  if (item.type === 'sms' && !channelAccess.sms) return false;
                  return isChannelVisible(item.type);
                }).map((item) => (
                  <li
                    key={item.id}
                    className="activity-item"
                    style={{ cursor: 'pointer', borderRadius: 8, padding: '14px 8px', transition: 'background 0.15s' }}
                    onClick={() => setSelectedActivity(item)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div className={`activity-icon ${item.type}`}>
                      {item.type === 'email' ? '📧' : item.type === 'whatsapp' ? '📱' : '💬'}
                    </div>
                    <div className="activity-details" style={{ flex: 1 }}>
                      <div className="activity-text">
                        <strong>{formatActivityCategory(item.category, item.messageType)}</strong> {item.type} sent to{' '}
                        {item.customer?.name || 'customer'}
                      </div>
                      {item.subject && (
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
                          {item.subject}
                        </div>
                      )}
                      {!item.subject && item.messageBody && (
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
                          {item.messageBody.substring(0, 80)}{item.messageBody.length > 80 ? '...' : ''}
                        </div>
                      )}
                      <div className="activity-time">
                        {formatDate(item.sentAt)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className={`badge ${getDeliveryBadgeClass(item)}`}
                        style={getDeliveryBadgeStyle(item)}>
                        {getDeliveryLabel(item)}
                      </span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 16 }}>›</span>
                    </div>
                  </li>
                ))}
              </ul>
              </div>
              {/* Pagination */}
              {activityTotal > ACTIVITY_PAGE_SIZE && (() => {
                const totalPages = Math.ceil(activityTotal / ACTIVITY_PAGE_SIZE);
                const getPageNumbers = () => {
                  const pages = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    if (activityPage > 3) pages.push('...');
                    for (let i = Math.max(2, activityPage - 1); i <= Math.min(totalPages - 1, activityPage + 1); i++) pages.push(i);
                    if (activityPage < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages;
                };
                const btnBase = {
                  border: '1px solid var(--border)', borderRadius: 6, padding: '5px 10px',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s ease',
                  background: 'var(--surface)', color: 'var(--text-secondary)',
                };
                return (
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 0 4px', borderTop: '1px solid var(--border)', marginTop: 12,
                  }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      Page {activityPage} of {totalPages} &middot; {activityTotal} total
                    </span>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button
                        style={{ ...btnBase, opacity: activityPage === 1 ? 0.4 : 1 }}
                        disabled={activityPage === 1 || activityLoading}
                        onClick={() => goToPage(activityPage - 1)}
                        onMouseEnter={e => { if (activityPage > 1) e.currentTarget.style.borderColor = 'var(--primary)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                      >
                        ‹ Prev
                      </button>
                      {getPageNumbers().map((p, i) =>
                        p === '...' ? (
                          <span key={`ellipsis-${i}`} style={{ fontSize: 12, color: 'var(--text-muted)', padding: '0 2px' }}>…</span>
                        ) : (
                          <button
                            key={p}
                            style={{
                              ...btnBase,
                              background: p === activityPage ? 'var(--primary)' : 'var(--surface)',
                              color: p === activityPage ? 'white' : 'var(--text-secondary)',
                              borderColor: p === activityPage ? 'var(--primary)' : 'var(--border)',
                              fontWeight: p === activityPage ? 700 : 500,
                            }}
                            disabled={activityLoading}
                            onClick={() => goToPage(p)}
                            onMouseEnter={e => { if (p !== activityPage) e.currentTarget.style.borderColor = 'var(--primary)'; }}
                            onMouseLeave={e => { if (p !== activityPage) e.currentTarget.style.borderColor = 'var(--border)'; }}
                          >
                            {p}
                          </button>
                        )
                      )}
                      <button
                        style={{ ...btnBase, opacity: activityPage === totalPages ? 0.4 : 1 }}
                        disabled={activityPage === totalPages || activityLoading}
                        onClick={() => goToPage(activityPage + 1)}
                        onMouseEnter={e => { if (activityPage < totalPages) e.currentTarget.style.borderColor = 'var(--primary)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                      >
                        Next ›
                      </button>
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>No activity yet</h3>
              <p>Import some customers to get started!</p>
            </div>
          )}
        </div>
      </div>

      {selectedActivity && (
        <ActivityDetailModal
          activity={selectedActivity}
          isDemo={isDemo}
          onClose={() => setSelectedActivity(null)}
        />
      )}

      {showQueued && (
        <QueuedMessagesModal
          range={range}
          isDemo={isDemo}
          onClose={() => setShowQueued(false)}
          onCountUpdate={setQueuedCountOverride}
        />
      )}

      {showLinkClicks && (
        <LinkClicksModal range={range} isDemo={isDemo} onClose={() => setShowLinkClicks(false)} />
      )}

      {showSentMessages && (
        <SentMessagesModal range={range} isDemo={isDemo} onClose={() => setShowSentMessages(false)} />
      )}
    </div>
  );
}
